// The view of a game one player is allowed to have.
//
// This is the whole of hidden information. The engine hides nothing from
// itself -- every card in every zone is a concrete object carrying its real
// id, which is what makes the rules tractable -- so when a state is sent to a
// browser, the parts that player may not know have to be taken out of it
// first. A client that is merely *told* not to look is not a card game.
//
// Two things are secret, and the second is the one that gets forgotten:
//
//   1. Card identities in a hand, a deck, or face down.
//   2. **The seed.** Every shuffle and draw is derived from it, so a client
//      that has the seed can compute the entire future of both decks. Blanking
//      the cards while sending the seed hides nothing at all.

import type { GameEvent, GameState, PlayerId, Zone } from './types.ts';

/**
 * The id a card wears when the viewer may not know what it is.
 *
 * A real entry in the card index rather than a null, so every path that draws a
 * card -- the layout, the library, the card back -- keeps working without a
 * special case. It has no art, so it is never playable and never appears in a
 * deck.
 */
export const HIDDEN_CARD = '__hidden';

/** Whether `viewer` is allowed to know what card is in `zone`. */
export function canSee(zone: Zone, owner: PlayerId, viewer: PlayerId | null): boolean {
  switch (zone.kind) {
    // 108. A hand is its owner's alone.
    case 'hand':
      return viewer === owner;
    // 106. A deck's contents *and its order* are secret from everyone,
    // including its owner -- knowing your next draw is not a thing the game
    // gives you, and the client has no business being able to show it.
    case 'mainDeck':
    case 'runeDeck':
      return false;
    // 811. A card hidden at a battlefield is known to the player who hid it.
    case 'facedown':
      return viewer === owner;
    // Everything else is public: the board, the trash, banishment, the chain,
    // and both designated zones.
    default:
      return true;
  }
}

/** Strips a card name out of an event the viewer should not learn from. */
function redactEvent(event: GameEvent, viewer: PlayerId | null): GameEvent {
  switch (event.t) {
    // "Opponent drew Fizz, Trickster" is the whole of the secret.
    case 'draw':
    case 'discard':
      return event.player === viewer ? event : { ...event, card: HIDDEN_CARD };
    // A channel is public -- the rune goes to the board face up.
    default:
      return event;
  }
}

/**
 * The state as `viewer` may see it. `null` is a spectator, who sees only what
 * is public to everyone.
 *
 * Returns a fresh object; the caller's state is untouched.
 */
export function redact(state: GameState, viewer: PlayerId | null): GameState {
  const view = structuredClone(state) as GameState;

  // The seed first, because it is the one that matters most and the easiest to
  // leave in by accident. Zeroed rather than removed so the shape still
  // type-checks on the far side.
  view.seed = 0;

  for (const object of Object.values(view.objects)) {
    if (!object) continue;
    if (!canSee(object.zone, object.owner, viewer)) object.card = HIDDEN_CARD;
  }

  view.events = view.events.map((e) => redactEvent(e, viewer));

  // 355.3. A question is only ever put to one player; the others should not
  // see its options, which name cards they may not know.
  if (view.pendingChoice && view.pendingChoice.player !== viewer) {
    view.pendingChoice = null;
  }
  // Answers already given belong to the resolution in progress, and replaying
  // one is the server's job, not a client's.
  view.choiceLog = [];
  view.triggerChoiceLog = [];

  return view;
}

/**
 * Whether a redacted state still names any card the viewer must not know.
 *
 * Exported so it can be asserted in tests rather than trusted: a leak here is
 * silent, and the failure mode is an opponent reading your hand.
 */
export function leaks(view: GameState, viewer: PlayerId | null): string[] {
  const found: string[] = [];
  if (view.seed !== 0) found.push('the seed is still present');
  for (const object of Object.values(view.objects)) {
    if (!object) continue;
    if (!canSee(object.zone, object.owner, viewer) && object.card !== HIDDEN_CARD) {
      found.push(`${object.zone.kind} card ${object.card} (object ${object.id})`);
    }
  }
  for (const event of view.events) {
    if ((event.t === 'draw' || event.t === 'discard')
      && event.player !== viewer && event.card !== HIDDEN_CARD) {
      found.push(`${event.t} event naming ${event.card}`);
    }
  }
  return found;
}
